import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { mediaUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { AddProductButton } from '@/components/cart/AddProductButton';
import { ImageIcon, ArrowRight, Store, Sparkles, Quote, Star, PackageX, SlidersHorizontal, Search, Truck, PackageCheck, MapPin, Clock } from 'lucide-react';

const eur = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

function ProductCard({ ownerId, p, interactive, shipping }) {
  const to = `/negozio/${ownerId}/${p.slug}`;
  const soldOut = p.product_type !== 'digital' && !p.in_stock;
  const isPhysical = p.product_type !== 'digital' && p.product_type !== 'virtual';
  const shipFrom = isPhysical && shipping && shipping.from_price != null ? shipping.from_price : null;
  return (
    <div className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm transition-transform duration-300 hover:-translate-y-1" data-testid={`store-product-${p.id}`}>
      <Link to={interactive ? to : '#'} onClick={(e) => { if (!interactive) e.preventDefault(); }} className="relative block aspect-square overflow-hidden bg-muted">
        {p.main_image ? (
          <img src={mediaUrl(p.main_image)} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full w-full items-center justify-center"><ImageIcon className="h-8 w-8 text-muted-foreground" /></div>
        )}
        {p.on_sale && <span className="absolute left-3 top-3 rounded-full bg-primary px-2.5 py-0.5 text-xs font-semibold text-primary-foreground">Offerta</span>}
        {soldOut && <span className="absolute right-3 top-3 rounded-full bg-foreground/80 px-2.5 py-0.5 text-xs font-semibold text-background">Esaurito</span>}
      </Link>
      <div className="flex flex-1 flex-col p-3">
        {p.category && <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{p.category}</span>}
        <Link to={interactive ? to : '#'} onClick={(e) => { if (!interactive) e.preventDefault(); }} className="line-clamp-2 text-sm font-medium text-foreground hover:text-primary">{p.name}</Link>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="font-display text-base font-bold text-foreground">{eur(p.effective_price)}</span>
          {p.on_sale && <span className="text-xs text-muted-foreground line-through">{eur(p.price)}</span>}
        </div>
        {shipFrom != null && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`store-product-ship-${p.id}`}>
            <Truck className="h-3 w-3" />+ spedizione da {eur(shipFrom)}
          </p>
        )}
        <div className="mt-3 flex-1" />
        <div className="mt-1">
          {!interactive ? (
            <Button size="sm" variant="outline" className="w-full" disabled>Aggiungi al carrello</Button>
          ) : soldOut ? (
            <Button size="sm" variant="outline" className="w-full" disabled><PackageX className="mr-1.5 h-4 w-4" />Esaurito</Button>
          ) : p.has_variants ? (
            <Button asChild size="sm" variant="outline" className="w-full"><Link to={to}><SlidersHorizontal className="mr-1.5 h-4 w-4" />Scegli opzioni</Link></Button>
          ) : (
            <AddProductButton ownerId={ownerId} productId={p.id} qty={1} title={p.name} size="sm" className="w-full" label="Aggiungi" />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Vista vetrina condivisa: usata sia dall'anteprima nell'editor CRM (interactive=false)
 * sia dalla pagina pubblica del negozio (interactive=true).
 */
export function StoreLandingView({ data, ownerId, interactive = true }) {
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  const d = data || {};
  const products = d.products || [];
  const categories = (d.categories || []).map((c) => (typeof c === 'string' ? { name: c, count: 0 } : c));
  const featured = d.featured || [];
  const gallery = (d.gallery || []).filter(Boolean);
  const shipping = d.shipping || null;

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return products.filter((p) => {
      const okCat = cat === 'all' || (p.category || 'Altro') === cat;
      if (!okCat) return false;
      if (!term) return true;
      const hay = `${p.name || ''} ${p.short_description || ''} ${p.category || ''}`.toLowerCase();
      return hay.includes(term);
    });
  }, [products, cat, q]);

  return (
    <div className="w-full max-w-full overflow-x-hidden" data-testid="store-landing">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-muted" data-testid="store-hero">
        <div className="relative aspect-[21/9] max-h-[420px] w-full">
          {d.hero_image ? (
            <img src={mediaUrl(d.hero_image)} alt={d.name} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5"><Store className="h-12 w-12 text-primary/40" /></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-8">
            <h1 className="font-display text-2xl font-bold tracking-tight text-white drop-shadow sm:text-4xl" data-testid="store-name">{d.name || 'Il tuo negozio'}</h1>
            {d.tagline && <p className="mt-1 max-w-2xl text-sm text-white/90 sm:text-base" data-testid="store-tagline">{d.tagline}</p>}
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-white/80">
              {typeof d.product_count === 'number' && <Badge variant="secondary" className="bg-white/15 text-white backdrop-blur">{d.product_count} prodotti</Badge>}
              {d.from_price != null && <Badge variant="secondary" className="bg-white/15 text-white backdrop-blur">da {eur(d.from_price)}</Badge>}
            </div>
          </div>
        </div>
      </div>

      {/* STIMA SPEDIZIONE (collegata a Packlink PRO) */}
      {interactive && shipping && (shipping.from_price != null || shipping.free_threshold != null || shipping.pickup) && (
        <section className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-2xl border border-border bg-card p-4 text-sm shadow-sm" data-testid="store-shipping-info">
          {shipping.from_price != null ? (
            <span className="flex items-center gap-2 font-medium text-foreground">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Truck className="h-4 w-4" /></span>
              Spedizione stimata da <span className="font-display font-bold">{eur(shipping.from_price)}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 font-medium text-foreground">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary"><Truck className="h-4 w-4" /></span>
              Spedizione in tutta Italia
            </span>
          )}
          {shipping.free_threshold != null && (
            <span className="flex items-center gap-1.5 text-muted-foreground"><PackageCheck className="h-4 w-4 text-primary" />Gratuita sopra {eur(shipping.free_threshold)}</span>
          )}
          {shipping.prep_days > 0 && (
            <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-4 w-4" />Pronta in {shipping.prep_days} {shipping.prep_days === 1 ? 'giorno' : 'giorni'}</span>
          )}
          {shipping.pickup && (
            <span className="flex items-center gap-1.5 text-muted-foreground"><MapPin className="h-4 w-4" />Ritiro in sede disponibile</span>
          )}
        </section>
      )}

      {/* STORIA */}
      {d.show_story !== false && d.story && (
        <section className="mt-8 rounded-2xl border border-border bg-card p-5 sm:p-7" data-testid="store-story">
          <div className="flex items-start gap-3">
            <Quote className="h-6 w-6 shrink-0 text-primary/60" />
            <p className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground sm:text-base">{d.story}</p>
          </div>
        </section>
      )}

      {/* IN EVIDENZA */}
      {d.show_featured !== false && featured.length > 0 && (
        <section className="mt-8" data-testid="store-featured">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-foreground"><Sparkles className="h-5 w-5 text-primary" />In evidenza</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {featured.map((p) => <ProductCard key={`f-${p.id}`} ownerId={ownerId} p={p} interactive={interactive} shipping={shipping} />)}
          </div>
        </section>
      )}

      {/* GALLERIA */}
      {d.show_gallery !== false && gallery.length > 0 && (
        <section className="mt-8" data-testid="store-gallery">
          <h2 className="font-display text-lg font-bold tracking-tight text-foreground">Galleria</h2>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {gallery.map((g, i) => (
              <div key={i} className="aspect-square overflow-hidden rounded-xl border border-border bg-muted">
                <img src={mediaUrl(g)} alt={`Galleria ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CATALOGO */}
      <section className="mt-8" data-testid="store-catalog">
        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold tracking-tight text-foreground">Catalogo</h2>
            {interactive && (
              <div className="relative w-full sm:w-72" data-testid="store-search">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca un prodotto…"
                  className="pl-9"
                  data-testid="store-search-input"
                  aria-label="Cerca un prodotto"
                />
              </div>
            )}
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="store-category-filter">
              <button type="button" onClick={() => setCat('all')} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${cat === 'all' ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/50'}`} data-testid="store-cat-all">Tutti</button>
              {categories.map((c) => (
                <button key={c.name} type="button" onClick={() => setCat(c.name)} className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${cat === c.name ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-muted-foreground hover:border-primary/50'}`} data-testid={`store-cat-${c.name}`}>{c.name}{c.product_count ? ` (${c.product_count})` : ''}</button>
              ))}
            </div>
          )}
        </div>

        {filtered.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-border bg-muted/40 p-10 text-center" data-testid="store-empty">
            <PackageX className="mx-auto h-9 w-9 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">{q.trim() ? `Nessun prodotto trovato per «${q.trim()}».` : 'Nessun prodotto in questa categoria.'}</p>
            {(q.trim() || cat !== 'all') && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setQ(''); setCat('all'); }} data-testid="store-reset-filters">Azzera filtri</Button>
            )}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="store-products-grid">
            {filtered.map((p) => <ProductCard key={p.id} ownerId={ownerId} p={p} interactive={interactive} shipping={shipping} />)}
          </div>
        )}
      </section>

      {/* RECENSIONI (teaser) */}
      {d.show_reviews !== false && (
        <section className="mt-8 flex items-center gap-2 rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground" data-testid="store-reviews-teaser">
          <Star className="h-4 w-4 text-amber-400" />
          Le recensioni verificate dei clienti sono visibili in ogni scheda prodotto.
        </section>
      )}
    </div>
  );
}

export default StoreLandingView;
